import {
    invariant,
    isArrayEqual,
    isBoolean,
    isNull,
    isNumber,
    isString,
    isUndefined,
} from "@multiweb/reactUtils";
import { ReduxCompatibleValue, TypedObject } from "@multiweb/types/react";
import { ControlReduxRawValue } from "../types";
import { IExplicitValidationError, ILocalizedExplicitValidationError } from "./types";
import {
    IIsNumberCharactersOnlyValidParams,
    IIsNumbersOnlyValidParams,
    IValidateCharsDeletedParams,
    IValidateMaxLengthParams,
    IValidateMaxValueParams,
    IValidateMinValueParams,
    IValidateRegexParams,
} from "./validationFunctionTypes";

export const isLocArgsEqual = (locArgs1: string[] | undefined, locArgs2: string[] | undefined) => {
    if (locArgs1 === locArgs2) {
        return true;
    } else if (locArgs1 === undefined || locArgs2 === undefined) {
        return false;
    } else {
        return isArrayEqual(locArgs1, locArgs2, Object.is);
    }
};

export const isErrorEqual = (error1: IExplicitValidationError, error2: IExplicitValidationError): boolean => (
    error1.locKey === error2.locKey &&
    isLocArgsEqual(error1.locArgs, error2.locArgs) &&
    error1.message === error2.message
);

export const areErrorsEqual = (errors1: ReadonlyArray<IExplicitValidationError>, errors2: ReadonlyArray<IExplicitValidationError>) => (
    isArrayEqual(errors1, errors2, isErrorEqual)
);

export const isLocalizedErrorEqual = (error1: ILocalizedExplicitValidationError, error2: ILocalizedExplicitValidationError): boolean => (
    error1.message === error2.message
);

export const areLocalizedErrorsEqual = (errors1: ReadonlyArray<ILocalizedExplicitValidationError>, errors2: ReadonlyArray<ILocalizedExplicitValidationError>) => (
    isArrayEqual(errors1, errors2, isLocalizedErrorEqual)
);

/**
 * Převádí univerzální parametry na konkrétní {@link IValidateMaxLengthParams}.
 * @param value Platná hodnota je `string|null|undefined`. Pokud jakákoli jiná, pak vyhodí chybu.
 * @param args Platný je objekt s položkou `maxLength: number`. Pokud tuto položku `args` nemá, pak vyhodí chybu.
 */
export const getTypedParamsForIsMaxLengthValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IValidateMaxLengthParams => {
    invariant(isString(value) || isUndefined(value) || isNull(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(!!args.maxLength, "[Controls/validation] maxLength not present");
    invariant(isNumber(args.maxLength), `[Controls/validation] maxLength is not a number: ${args.maxLength}`);
    return ({
        value,
        ...args,
    }) as IValidateMaxLengthParams;
};

/**
 * Převádí univerzální parametry na konkrétní {@link IValidateCharsDeletedParams}.
 * @param value Platná hodnota je `string|null|undefined`. Pokud jakákoli jiná, pak vyhodí chybu.
 * @param args Platný je objekt s položkou `targetValue: string|null|undefined`.
 */
export const getTypedParamsForCharsDeletedValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IValidateCharsDeletedParams => {
    invariant(isString(value) || isUndefined(value) || isNull(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(isString(args.targetValue) || isUndefined(args.targetValue) || isNull(args.targetValue), `[Controls/validation] Unexpected targetValue: ${args.targetValue}`);
    return ({
        value,
        targetValue: args.targetValue,
    }) as IValidateCharsDeletedParams;
};

/**
 * Převádí univerzální parametry na konkrétní {@link IValidateRegexParams}.
 * @param value Platná hodnota je `string|null|undefined`. Pokud jakákoli jiná, pak vyhodí chybu.
 * @param args Platný je jednoprvkový objekt s položkou `regex: string` obsahující platný regulární výraz. Pokud tuto položku `args` nemá, pak vyhodí chybu.
 */
export const getTypedParamsForIsRegexValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IValidateRegexParams => {
    invariant(isString(value) || isUndefined(value) || isNull(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(!!args.regex, "[Controls/validation] regex not present");
    invariant(isString(args.regex), `[Controls/validation] regex is not a string: ${args.regex}`);
    return ({
        value,
        regex: new RegExp(args.regex),
    }) as IValidateRegexParams;
};

/**
 * Převádí univerzální parametry na konkrétní {@link IValidateMinValueParams}.
 * @param value Platná hodnota je `string`. Jakákoliv jiná hodnota vyhodí chybu.
 * @param args Platný je dvouprvkový objekt s položkami:
 * - `isBigNumber` - příznak zda se má validovat hodnota jako BigNumber
 * - `minValue` - největší validní hodnota
 */
export const getTypedParamsForIsMinValueValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IValidateMinValueParams => {
    invariant(isString(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(args.isBigNumber != null, "[Controls/validation] isBigNumber not present");
    invariant(args.minValue != null, "[Controls/validation] minValue not present");
    invariant(isBoolean(args.isBigNumber), `[Controls/validation] isBigNumber is not a boolean: ${value}`);
    invariant(isNumber(args.minValue), `[Controls/validation] minValue is not a number: ${value}`);
    return ({
        value,
        isBigNumber: args.isBigNumber,
        minValue: args.minValue,
    });
};

/**
 * Převádí univerzální parametry na konkrétní {@link IValidateMaxValueParams}.
 * @param value Platná hodnota je `string`. Jakákoliv jiná hodnota vyhodí chybu.
 * @param args Platný je dvouprvkový objekt s položkami:
 * - `isBigNumber` - příznak zda se má validovat hodnota jako BigNumber
 * - `maxValue` - největší validní hodnota
 */
export const getTypedParamsForIsMaxValueValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IValidateMaxValueParams => {
    invariant(isString(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(args.isBigNumber != null, "[Controls/validation] isBigNumber not present");
    invariant(args.maxValue != null, "[Controls/validation] maxValue not present");
    invariant(isBoolean(args.isBigNumber), `[Controls/validation] isBigNumber is not a boolean: ${value}`);
    invariant(isNumber(args.maxValue), `[Controls/validation] maxValue is not a number: ${value}`);
    return ({
        value,
        isBigNumber: args.isBigNumber,
        maxValue: args.maxValue,
    });
};

/**
 * Převádí univerzální parametry na konkrétní {@link IIsNumbersOnlyValidParams}.
 * @param value Platná hodnota je `string|null|undefined`. Pokud jakákoli jiná, pak vyhodí chybu.
 * @param args Platný je prázdný objekt. (Aneb, žádné parametry se neočekávají.) Pokud `args` není prázdný, pak vyhodí chybu.
 */
export const getTypedParamsForIsNumbersOnlyValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IIsNumbersOnlyValidParams => {
    invariant(isString(value) || isUndefined(value) || isNull(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(Object.keys(args).length === 0, "[Controls/validation] args is not empty");
    return ({
        value,
    }) as IIsNumbersOnlyValidParams;
};

/**
 * Převádí univerzální parametry na konkrétní {@link IIsNumberCharactersOnlyValidParams}.
 * @param value Platná hodnota je `string`. Jakákoliv jiná hodnota vyhodí chybu.
 * @param args Platný je jednoprvkový objekt s položkou `isInteger: boolean` obsahující příznak, zda se mají validovat pouze celá čísla.
 * Pokud tuto položku `args` nemá, pak vyhodí chybu.
 */
export const getTypedParamsForIsNumberCharactersOnlyValidOrFail = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
): IIsNumberCharactersOnlyValidParams => {
    invariant(isString(value), `[Controls/validation] Unexpected value: ${value}`);
    invariant(args.isInteger != null, "[Controls/validation] isInteger is not present");
    invariant(isBoolean(args.isInteger), "[Controls/validation] isInteger is not a boolean.");
    return ({
        value,
        isInteger: args.isInteger,
    }) as IIsNumberCharactersOnlyValidParams;
};
